import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../utils/cn";

// A pill never shrinks or wraps its label: it grows to fit the text.
const statusBadgeVariants = cva("inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-medium", {
    variants: {
        variant: {
            // status
            success:
                "border-success/20 bg-success/10 text-success dark:border-green-400/30 dark:bg-green-400/10 dark:text-green-400",
            destructive:
                "border-destructive/20 bg-destructive/10 text-destructive dark:border-destructive/40 dark:bg-destructive/15 dark:text-red-400",
            // For the status that must not be missed: twice `destructive`'s tint, a step darker.
            critical:
                "border-destructive/40 bg-destructive/20 text-destructive dark:border-destructive/60 dark:bg-destructive/30 dark:text-red-300",
            warning:
                "border-warning/30 bg-warning/10 text-amber-700 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-400",
            muted: "border-border bg-muted text-muted-foreground",
            // A highlight in the brand's teal, for good news that is not a status (a birthday). Navy
            // text on light: teal text on the teal tint is under 4.5:1.
            brand: "border-secondary/30 bg-secondary/10 text-primary [&_svg]:text-text-link dark:border-secondary/40 dark:bg-secondary/15 dark:text-text-link",
            // component
            tag: "border-border bg-muted text-muted-foreground",
            // roles — light mode uses a pale tint; dark mode swaps to a deep tint with light text
            // instead of relying on the raw Tailwind palette (which never adapts to .dark).
            pink: "border-pink-200 bg-pink-50 text-pink-600 dark:border-pink-800/60 dark:bg-pink-950/40 dark:text-pink-300",
            violet: "border-violet-200 bg-violet-50 text-purple-600 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-purple-300",
            sky: "border-sky-200 bg-sky-50 text-sky-600 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-300",
            amber: "border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300",
            teal: "border-teal-200 bg-teal-50 text-teal-600 dark:border-teal-800/60 dark:bg-teal-950/40 dark:text-teal-300",
            indigo: "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-800/60 dark:bg-indigo-950/40 dark:text-indigo-300",
            emerald:
                "border-emerald-200 bg-emerald-50 text-emerald-600 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300",
        },
        size: {
            xs: "gap-1 px-2 py-0.5 text-[0.6875rem]",
            sm: "gap-1.5 px-3 py-1 text-xs",
            lg: "justify-center min-w-[2.5rem] px-3 py-1.5 text-sm font-semibold",
            // Bare dot and label: the variants' `dark:` tints would otherwise draw a pill in dark mode.
            ghost: "gap-1.5 border-transparent bg-transparent px-0 py-0 text-sm shadow-none dark:border-transparent dark:bg-transparent",
        },
    },
    defaultVariants: {
        variant: "muted",
        size: "sm",
    },
});

const dotVariants: Record<string, string> = {
    success: "bg-success dark:bg-green-400",
    destructive: "bg-destructive",
    critical: "bg-destructive",
    warning: "bg-warning",
    muted: "bg-muted-foreground",
    brand: "bg-secondary",
    pink: "bg-pink-500",
    violet: "bg-purple-500",
    sky: "bg-sky-500",
    amber: "bg-amber-500",
    teal: "bg-teal-500",
    indigo: "bg-indigo-800 dark:bg-indigo-400",
    tag: "bg-secondary",
    emerald: "bg-emerald-600 dark:bg-emerald-400",
};

export interface StatusBadgeProps
    extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof statusBadgeVariants> {
    textClassName?: string;
    onRemove?: () => void;
    /** Renders a small colored dot before the text. Off by default. */
    dot?: boolean;
}

const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps>(
    ({ className, variant = "muted", size = "sm", textClassName, children, onRemove, dot = false, ...props }, ref) => (
        <span ref={ref} className={cn(statusBadgeVariants({ variant, size }), className)} {...props}>
            {dot && <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", dotVariants[variant ?? "muted"])} />}
            <span className={textClassName}>{children}</span>
            {onRemove && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="hover:text-foreground -mr-0.5 leading-none transition-colors"
                    aria-label="Remove"
                >
                    ×
                </button>
            )}
        </span>
    ),
);
StatusBadge.displayName = "StatusBadge";

export { StatusBadge, statusBadgeVariants };
